import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, id, ...rest }, ref) => {
    const inputId = id ?? rest.name;
    const descId = inputId ? `${inputId}-desc` : undefined;
    return (
      <label className="block">
        {label && (
          <span className="mb-2 block text-[11px] uppercase tracking-widest text-makhe-muted">
            {label}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={(error || hint) && descId ? descId : undefined}
          className={cn(
            'w-full border-0 border-b border-makhe-ink/40 bg-transparent px-0 py-2 text-makhe-ink placeholder:text-makhe-muted/60 focus:border-makhe-ink focus:outline-none focus:ring-0 transition-colors',
            error && 'border-makhe-plum',
            className,
          )}
          {...rest}
        />
        {hint && !error && <span id={descId} className="mt-1 block text-[11px] text-makhe-muted">{hint}</span>}
        {error && <span id={descId} role="alert" className="mt-1 block text-[11px] text-makhe-plum">{error}</span>}
      </label>
    );
  },
);
Input.displayName = 'Input';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...rest }, ref) => {
    const inputId = id ?? rest.name;
    const descId = inputId ? `${inputId}-desc` : undefined;
    return (
      <label className="block">
        {label && (
          <span className="mb-2 block text-[11px] uppercase tracking-widest text-makhe-muted">
            {label}
          </span>
        )}
        <textarea
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={(error || hint) && descId ? descId : undefined}
          className={cn(
            'w-full rounded-md border border-makhe-line bg-makhe-paper/60 px-3 py-2 text-makhe-ink placeholder:text-makhe-muted/60 focus:border-makhe-ink focus:outline-none focus:ring-0 transition-colors',
            error && 'border-makhe-plum',
            className,
          )}
          {...rest}
        />
        {hint && !error && <span id={descId} className="mt-1 block text-[11px] text-makhe-muted">{hint}</span>}
        {error && <span id={descId} role="alert" className="mt-1 block text-[11px] text-makhe-plum">{error}</span>}
      </label>
    );
  },
);
Textarea.displayName = 'Textarea';

interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, className, id, ...rest }, ref) => {
    const inputId = id ?? rest.name;
    const descId = inputId ? `${inputId}-desc` : undefined;
    return (
      <label className="block">
        {label && (
          <span className="mb-2 block text-[11px] uppercase tracking-widest text-makhe-muted">
            {label}
          </span>
        )}
        <select
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && descId ? descId : undefined}
          className={cn(
            'w-full rounded-md border border-makhe-line bg-makhe-paper/60 px-3 py-2 text-makhe-ink focus:border-makhe-ink focus:outline-none focus:ring-0 transition-colors',
            error && 'border-makhe-plum',
            className,
          )}
          {...(rest as Record<string, unknown>)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error && <span id={descId} role="alert" className="mt-1 block text-[11px] text-makhe-plum">{error}</span>}
      </label>
    );
  },
);
Select.displayName = 'Select';
